import React, { useContext, useState } from "react";
import { UserContext } from "../context/MainContext";
import { useNavigate } from "react-router-dom";
import {
  CLOUDINARY_CLOUD,
  CLOUDINARY_PRESET,
} from "../constants/CloudinaryConstants";
import { HOST } from "../constants/HostConstants";
import { profilePromotionPurchase } from "../actions/profilePromotionPurchase";

const AdStore = () => {
  const { user, setUser } = useContext(UserContext);
  const nav = useNavigate();
  const [activeTab, setActiveTab] = useState("banners");
  const [selectedFormat, setSelectedFormat] = useState(null);
  const [uploadedImage, setUploadedImage] = useState(null);
  const [adLink, setAdLink] = useState("");
  const [isUploading, setIsUploading] = useState(false);
  const [formError, setFormError] = useState("");
  const [preview, setPreview] = useState(null);
  const [promotionMessage, setPromotionMessage] = useState("");

  // Ad format specifications
  const adFormats = {
    vertical: {
      name: "Vertical Banner",
      width: 300,
      height: 600,
      price: 100,
      description: "Tall vertical ad for sidebars and property pages",
    },
    horizontal: {
      name: "Horizontal Banner",
      width: 728,
      height: 90,
      price: 100,
      description: "Wide horizontal ad for page headers and property sections",
    },
  };

  // Check if user can create ads
  const canCreateAds =
    user?.account_type === "Premium" &&
    (user?.class_type === "Realtor" || user?.class_type === "Builder");
  const isAdmin = user?.class_type === "Admin";
  const hasAccess = canCreateAds || isAdmin;
  const canPurchasePromotions = canCreateAds || isAdmin;

  if (!hasAccess) {
    return (
      <div className="mt-8 px-8 pb-16">
        <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded">
          <h2 className="text-xl font-bold text-blue-900 mb-2">
            ℹ️ Advertisement Access Limited
          </h2>
          <p className="text-blue-800 mb-3">
            Currently, only Real Estate Sales Agents and Builder Sales Agents
            can create global advertisements.
          </p>
          <p className="text-blue-800">
            If you represent a Title Company, Lender, or other service, please
            <a href="/contact" className="text-blue-600 underline ml-1">
              request advertisement placement
            </a>
            .
          </p>
        </div>
      </div>
    );
  }

  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith("image/")) {
      setFormError("Please upload a valid image file");
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setFormError("Image must be less than 5MB");
      return;
    }

    setUploadedImage(file);
    setFormError("");

    // Show preview
    const reader = new FileReader();
    reader.onload = (e) => {
      setPreview(e.target?.result);
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError("");

    // Validation
    if (!selectedFormat) {
      setFormError("Please select an ad format");
      return;
    }
    if (!uploadedImage) {
      setFormError("Please upload an image");
      return;
    }
    if (!adLink.trim()) {
      setFormError("Please enter a link URL");
      return;
    }

    const selectedFormatData = adFormats[selectedFormat];
    const requiredKeys = selectedFormatData.price;

    if ((user?.key_balance || 0) < requiredKeys) {
      setFormError(
        `Insufficient keys. You need ${requiredKeys} keys but have ${user?.key_balance || 0}.`,
      );
      return;
    }

    setIsUploading(true);
    try {
      // Upload image to Cloudinary
      if (!CLOUDINARY_CLOUD || !CLOUDINARY_PRESET) {
        throw new Error("Cloudinary configuration is missing");
      }

      const formData = new FormData();
      formData.append("file", uploadedImage);
      formData.append("upload_preset", CLOUDINARY_PRESET);

      const cloudinaryRes = await fetch(
        `https://api.cloudinary.com/v1_1/${CLOUDINARY_CLOUD}/image/upload`,
        {
          method: "POST",
          body: formData,
        },
      );

      if (!cloudinaryRes.ok) {
        throw new Error("Failed to upload image to Cloudinary");
      }

      const cloudinaryData = await cloudinaryRes.json();
      const imageUrl = cloudinaryData.public_id;

      // Submit ad request to backend
      const response = await fetch(`${HOST}/api/advertisement/purchaseAd`, {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: user?.user_id,
          advertisement_url: imageUrl,
          alignment: selectedFormat,
          advertisement_type: "Banner",
          link_url: adLink,
          format_width: selectedFormatData.width,
          format_height: selectedFormatData.height,
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || "Failed to purchase ad");
      }

      const result = await response.json();

      // Update local key balance
      const adPrice = adFormats[selectedFormat].price;
      setUser({
        ...user,
        key_balance: (user?.key_balance || 0) - adPrice,
      });

      alert(
        "Advertisement submitted successfully! Your ad is pending admin approval and will go live once approved.",
      );

      // Reset form
      setSelectedFormat(null);
      setUploadedImage(null);
      setAdLink("");
      setPreview(null);

      // Redirect to dashboard
      setTimeout(() => nav("/ad-dashboard"), 1500);
    } catch (error) {
      setFormError(error.message || "Failed to purchase advertisement");
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="mt-8 px-8 pb-16">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-4xl font-bold mb-2">Advertisement Store</h1>
        <p className="text-gray-600">
          Create and purchase global advertisements for real estate properties
        </p>
      </div>

      {/* Tabs for Admins */}
      {hasAccess && (
        <div className="mb-8 border-b border-gray-200">
          <div className="flex gap-4">
            <button
              onClick={() => setActiveTab("banners")}
              className={`py-3 px-4 font-semibold border-b-2 transition-all ${
                activeTab === "banners"
                  ? "border-blue-600 text-blue-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              Banner Ads
            </button>
            {canPurchasePromotions && (
              <button
                onClick={() => setActiveTab("promotions")}
                className={`py-3 px-4 font-semibold border-b-2 transition-all ${
                  activeTab === "promotions"
                    ? "border-blue-600 text-blue-600"
                    : "border-transparent text-gray-600 hover:text-gray-900"
                }`}
              >
                Profile Promotions
              </button>
            )}
          </div>
        </div>
      )}

      {/* Banner Ads Tab */}
      {activeTab === "banners" && (
        <>
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-4">
              Step 1: Choose Ad Format
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Object.entries(adFormats).map(([key, format]) => (
                <div
                  key={key}
                  onClick={() => setSelectedFormat(key)}
                  className={`p-6 border-2 rounded-lg cursor-pointer transition-all ${
                    selectedFormat === key
                      ? "border-blue-600 bg-blue-50"
                      : "border-gray-300 bg-white hover:border-blue-400"
                  }`}
                >
                  <h3 className="text-xl font-bold mb-2">{format.name}</h3>
                  <div className="bg-gray-200 rounded mb-3 flex items-center justify-center h-32">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-gray-700">
                        {format.width}x{format.height}px
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {format.description}
                      </p>
                    </div>
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-sm text-gray-700">
                      <span className="font-semibold">Cost:</span>{" "}
                      <span className="text-green-600 font-bold">
                        {format.price} Keys
                      </span>
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      One-time payment • Ad runs indefinitely • Shuffles on each
                      visit
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {selectedFormat && (
            <>
              {/* Upload Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">
                  Step 2: Upload Image
                </h2>
                <div className="bg-white border-2 border-dashed border-gray-300 rounded-lg p-8">
                  <div className="text-center">
                    <input
                      type="file"
                      accept="image/*"
                      onChange={handleImageUpload}
                      className="hidden"
                      id="image-upload"
                      disabled={isUploading}
                    />
                    <label htmlFor="image-upload" className="cursor-pointer">
                      <div className="bg-gray-100 rounded-lg p-12 hover:bg-gray-200 transition-colors">
                        <svg
                          className="mx-auto h-12 w-12 text-gray-400 mb-2"
                          stroke="currentColor"
                          fill="none"
                          viewBox="0 0 48 48"
                        >
                          <path
                            d="M28 8H12a4 4 0 00-4 4v20a4 4 0 004 4h24a4 4 0 004-4V20m-14-8l6 6m0 0l-6 6m6-6H6"
                            strokeWidth={2}
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <p className="text-lg font-semibold text-gray-700 mb-1">
                          Click to upload image
                        </p>
                        <p className="text-sm text-gray-500">
                          Recommended size: {adFormats[selectedFormat].width}x
                          {adFormats[selectedFormat].height}px
                        </p>
                        <p className="text-xs text-gray-400 mt-2">
                          JPG, PNG, or GIF • Max 5MB
                        </p>
                      </div>
                    </label>
                  </div>

                  {/* Image Preview */}
                  {preview && (
                    <div className="mt-6 text-center">
                      <p className="text-sm font-semibold text-gray-700 mb-3">
                        Preview
                      </p>
                      <img
                        src={preview}
                        alt="Preview"
                        className="mx-auto max-h-48 rounded border"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* Link Section */}
              <div className="mb-8">
                <h2 className="text-2xl font-bold mb-4">
                  Step 3: Add Link URL
                </h2>
                <input
                  type="url"
                  placeholder="https://example.com"
                  value={adLink}
                  onChange={(e) => setAdLink(e.target.value)}
                  disabled={isUploading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-600 mt-2">
                  Users will be directed to this URL when clicking your ad
                </p>
              </div>

              {/* Error Display */}
              {formError && (
                <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-red-700 font-semibold">{formError}</p>
                </div>
              )}

              {/* Summary and Submit */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-bold mb-4">Purchase Summary</h3>
                <div className="space-y-2 mb-4">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Ad Format:</span>
                    <span className="font-semibold">
                      {adFormats[selectedFormat].name}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Cost:</span>
                    <span className="font-bold text-green-600">
                      {adFormats[selectedFormat].price} Keys
                    </span>
                  </div>
                  <div className="flex justify-between border-t pt-2">
                    <span className="text-gray-700">Your Balance:</span>
                    <span
                      className={`font-bold ${(user?.key_balance || 0) >= adFormats[selectedFormat].price ? "text-green-600" : "text-red-600"}`}
                    >
                      {user?.key_balance || 0} Keys
                    </span>
                  </div>
                </div>
                <button
                  onClick={handleSubmit}
                  disabled={isUploading || !uploadedImage || !adLink.trim()}
                  className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                    isUploading || !uploadedImage || !adLink.trim()
                      ? "bg-gray-400 cursor-not-allowed"
                      : "bg-blue-600 hover:bg-blue-700"
                  }`}
                >
                  {isUploading ? "Processing..." : "Purchase Advertisement"}
                </button>
              </div>
            </>
          )}

          {/* Info Section */}
          <div className="bg-blue-50 border-l-4 border-blue-500 p-6 rounded mt-12">
            <h3 className="text-lg font-bold text-blue-900 mb-3">
              How It Works
            </h3>
            <ul className="space-y-2 text-blue-800">
              <li>✓ Choose vertical or horizontal ad format</li>
              <li>✓ Upload your image (must match recommended dimensions)</li>
              <li>✓ Add a clickthrough link</li>
              <li>✓ Purchase with keys - ad runs indefinitely</li>
              <li>✓ Ads shuffle randomly on each page visit</li>
              <li>✓ Track impressions in your Ad Dashboard</li>
            </ul>
          </div>
        </>
      )}

      {/* Profile Promotions Tab */}
      {activeTab === "promotions" && canPurchasePromotions && (
        <div>
          {/* Header */}
          <div className="mb-8">
            <h2 className="text-2xl font-bold mb-2">Profile Promotion Store</h2>
            <p className="text-gray-600">
              Promote user profiles on all property listing pages
            </p>
          </div>

          {/* Profile Promotions Info */}
          <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-8 mb-8">
            <h3 className="text-xl font-bold text-purple-900 mb-4">
              What is a Profile Promotion?
            </h3>
            <p className="text-purple-800 mb-4">
              Promote a user's profile on all property listing pages with their
              professional information and contact details.
            </p>
            <div className="bg-white rounded p-4 mb-4 border border-purple-100">
              <p className="text-sm text-gray-700 font-semibold mb-3">
                Example Format:
              </p>
              <div className="text-center">
                <div className="text-sm font-semibold text-gray-800">
                  John Doe
                </div>
                <div className="text-xs text-gray-600">ABC Realty</div>
                <div className="text-xs text-gray-600">Real Estate Agent</div>
                <div className="text-xs text-gray-600">(555) 123-4567</div>
              </div>
            </div>
            <ul className="space-y-2 text-purple-800 text-sm">
              <li>✓ Displays above comments on all property pages</li>
              <li>✓ 7,500 impressions or until promotional slots are filled</li>
              <li>
                ✓ Shows: Profile photo, name, company, position, and phone
              </li>
              <li>✓ Keys deducted only when promotion is accepted</li>
            </ul>
          </div>

          {/* Purchase Option */}
          <div className="bg-white border border-gray-200 rounded-lg p-8">
            <h3 className="text-lg font-bold mb-6">
              Select User for Promotion
            </h3>
            <div className="mb-6">
              <label className="block text-gray-700 font-semibold mb-2">
                User ID to Promote <span className="text-red-500">*</span>
              </label>
              <input
                type="number"
                placeholder="Enter user ID"
                value={user?.selected_promotion_user_id || ""}
                onChange={(e) => {
                  setUser({
                    ...user,
                    selected_promotion_user_id: e.target.value,
                  });
                }}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
              />
              <p className="text-sm text-gray-600 mt-2">
                The profile will display on all property pages for 7,500
                impressions.
              </p>
            </div>

            {/* Cost Display */}
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-700">Promotion Type:</span>
                  <span className="font-semibold">Profile Display</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Impressions:</span>
                  <span className="font-semibold">7,500</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-700">Cost:</span>
                  <span className="font-bold text-green-600">250 Keys</span>
                </div>
                <div className="flex justify-between border-t pt-2">
                  <span className="text-gray-700">Your Balance:</span>
                  <span
                    className={`font-bold ${(user?.key_balance || 0) >= 250 ? "text-green-600" : "text-red-600"}`}
                  >
                    {user?.key_balance || 0} Keys
                  </span>
                </div>
              </div>
            </div>

            {/* Error/Success Display */}
            {formError && (
              <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-red-700 font-semibold">{formError}</p>
              </div>
            )}
            {promotionMessage && (
              <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
                <p className="text-green-700 font-semibold">
                  {promotionMessage}
                </p>
              </div>
            )}

            {/* Submit Button */}
            <button
              onClick={async () => {
                setFormError("");
                setPromotionMessage("");

                if (!user?.selected_promotion_user_id) {
                  setFormError("Please enter a user ID");
                  return;
                }

                if ((user?.key_balance || 0) < 250) {
                  setFormError(
                    `Insufficient keys. You need 250 keys but have ${user?.key_balance || 0}.`,
                  );
                  return;
                }

                setIsUploading(true);
                try {
                  const result = await profilePromotionPurchase(
                    parseInt(user.selected_promotion_user_id),
                    7500,
                  );

                  if (result.success) {
                    setPromotionMessage(
                      "Profile promotion purchased successfully! Your promotion is now pending admin approval.",
                    );
                    setUser({
                      ...user,
                      key_balance: result.new_balance,
                      selected_promotion_user_id: "",
                    });
                    setTimeout(() => nav("/dashboard"), 2000);
                  } else {
                    setFormError(
                      result.error || "Failed to purchase promotion",
                    );
                  }
                } catch (error) {
                  setFormError(error.message || "Failed to purchase promotion");
                } finally {
                  setIsUploading(false);
                }
              }}
              disabled={isUploading || !user?.selected_promotion_user_id}
              className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                isUploading || !user?.selected_promotion_user_id
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-purple-600 hover:bg-purple-700"
              }`}
            >
              {isUploading
                ? "Processing..."
                : "Purchase Profile Promotion (250 Keys)"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdStore;
